import { useMemo } from "react";
import { useSelector } from "react-redux";

import { tenantIdSelector } from "@/react/features/customer/customerSlice";
import {
  dataMerge,
  useAgentsData,
  useQueuesData,
} from "@/react/features/vcc/common";
import { useErrorNotification } from "@/react/notifications";

import { useFindAllAgentQueueAssignmentsForTenantQuery } from "./api";
import useAgentAssignmentsQueryFactory from "./useAgentAssignmentsQueryFactory";

const useAgentAssignmentsData = () => {
  const tenantName = useSelector(tenantIdSelector);

  const {
    data: assignments,
    isFetching: isFetchingAssignments,
    error: assignmentsError,
    refetch: refetchAssignments,
  } = useFindAllAgentQueueAssignmentsForTenantQuery({
    tenantName,
  });
  const {
    data: agents,
    isFetching: isFetchingAgents,
    error: agentsError,
    refetch: refetchAgents,
    queryFunctions: { getAgentById, getAgentByUsername },
  } = useAgentsData();
  const {
    data: queues,
    isFetching: isFetchingQueues,
    error: queuesError,
    refetch: refetchQueues,
    queryFunctions: { getQueueById, getQueueByName },
  } = useQueuesData();

  const allQueriesFinished =
    !isFetchingAssignments && !isFetchingAgents && !isFetchingQueues;

  // TODO: Perhaps we should combine the errors from all three APIs into one that is more user friendly and concise
  useErrorNotification(assignmentsError);

  // Merge the data between the three queries
  const modifiedAssignments = useMemo(() => {
    if (allQueriesFinished) {
      const data = dataMerge({
        localData: assignments,
        remoteData: agents,
        localMember: "agentName",
        remoteMember: "sso.sso_username",
        commonLocalMember: "agentId",
        commonRemoteMember: "sso.sso_id",
      });
      return dataMerge({
        localData: data,
        remoteData: queues,
        localMember: "queueName",
        remoteMember: "name",
        commonLocalMember: "queueId",
        commonRemoteMember: "id",
      });
    }
    return [];
  }, [allQueriesFinished]);

  const queryFunctionsFactory = useAgentAssignmentsQueryFactory(
    agents,
    queues,
    getAgentById,
    getQueueById
  );

  return {
    data: modifiedAssignments,
    isFetching: isFetchingAssignments || isFetchingAgents || isFetchingQueues,
    hasError: !!assignmentsError || !!agentsError || !!queuesError,
    refetch: () =>
      Promise.all([refetchAssignments(), refetchAgents(), refetchQueues()]),
    queryFunctions: {
      getAgentById,
      getAgentByUsername,
      getQueueById,
      getQueueByName,
      factory: queryFunctionsFactory,
    },
  };
};

export default useAgentAssignmentsData;
